import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import NRSelect from '../../../NRControlledSelect/NRSelect';
import NRJiraAssigneeSelect from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraAssigneeSelect';
import NRJiraPrioritySelect from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraPrioritySelect';
import { CREATE_JIRA_TICKET_LABELS } from './createJiraTicket.constants';
import NRJiraDynamicForm from './NRJiraDynamicForm';

const FIELD_SX = { width: '100%' };

function getStepHighlightSx(theme, isHighlighted) {
  if (!isHighlighted) {
    return undefined;
  }

  return {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
      borderWidth: 2
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
      borderWidth: 2
    }
  };
}

function NRCreateVulnerabilityJiraTicketFields({ form }) {
  const {
    spaceId,
    issueTypeId,
    summary,
    description,
    priority,
    assigneeId,
    spaceOptions,
    issueTypeOptions,
    priorityOptions,
    projectUsers,
    showPriorityField,
    priorityRequired,
    assigneeRequired,
    fieldsAfterSpaceEnabled,
    fieldsAfterIssueTypeEnabled,
    isIssueTypesLoading = false,
    isIssueFieldsLoading = false,
    issueFields,
    rpcOptions,
    dynamicFormRef,
    handleSpaceChange,
    handleIssueTypeChange,
    setSummary,
    setDescription,
    setPriority,
    setAssigneeId,
    setDynamicFieldsValid,
    handleRefreshIssueFields
  } = form;

  const theme = useTheme();
  const highlightSpace = !spaceId;
  const highlightIssueType = Boolean(spaceId) && !issueTypeId && fieldsAfterSpaceEnabled && !isIssueTypesLoading;

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: theme.spacing(2)
        }}
      >
        <NRSelect
          id="create-jira-ticket-space"
          label={CREATE_JIRA_TICKET_LABELS.JIRA_SPACE}
          value={spaceId}
          onChange={handleSpaceChange}
          options={spaceOptions}
          showEmptyValue={false}
          color="secondary"
          autoFocus={highlightSpace}
          sxProps={FIELD_SX}
          sx={getStepHighlightSx(theme, highlightSpace)}
        />

        <NRSelect
          id="create-jira-ticket-issue-type"
          label={CREATE_JIRA_TICKET_LABELS.ISSUE_TYPE}
          value={issueTypeId}
          onChange={handleIssueTypeChange}
          options={issueTypeOptions}
          disabled={!fieldsAfterSpaceEnabled || isIssueTypesLoading}
          showEmptyValue={false}
          color="secondary"
          autoFocus={highlightIssueType}
          sxProps={FIELD_SX}
          sx={getStepHighlightSx(theme, highlightIssueType)}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: showPriorityField ? '1fr 1fr' : '1fr',
          gap: theme.spacing(2)
        }}
      >
        {showPriorityField && (
          <NRJiraPrioritySelect
            id="create-jira-ticket-priority"
            label={CREATE_JIRA_TICKET_LABELS.PRIORITY}
            value={priority}
            onChange={setPriority}
            priorityOptions={priorityOptions}
            disabled={!fieldsAfterIssueTypeEnabled || isIssueFieldsLoading}
            required={priorityRequired}
            sxProps={FIELD_SX}
          />
        )}

        <NRJiraAssigneeSelect
          id="create-jira-ticket-assignee"
          label={CREATE_JIRA_TICKET_LABELS.ASSIGNEE}
          value={assigneeId}
          onChange={setAssigneeId}
          users={projectUsers}
          disabled={!fieldsAfterIssueTypeEnabled || isIssueFieldsLoading}
          required={assigneeRequired}
          sxProps={FIELD_SX}
        />
      </Box>

      <TextField
        id="create-jira-ticket-summary"
        label={CREATE_JIRA_TICKET_LABELS.SUMMARY}
        value={summary}
        onChange={event => setSummary(event.target.value)}
        disabled={!fieldsAfterIssueTypeEnabled}
        required
        fullWidth
        color="secondary"
      />

      <TextField
        id="create-jira-ticket-description"
        label={CREATE_JIRA_TICKET_LABELS.DESCRIPTION}
        value={description}
        onChange={event => setDescription(event.target.value)}
        disabled={!fieldsAfterIssueTypeEnabled}
        fullWidth
        multiline
        minRows={6}
        color="secondary"
      />

      {isIssueFieldsLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            color: theme.palette.text.secondary
          }}
        >
          <CircularProgress size={theme.spacing(2)} color="secondary" aria-label={CREATE_JIRA_TICKET_LABELS.LOADING_ISSUE_FIELDS} />
          <Typography variant="body2" color="text.secondary">
            {CREATE_JIRA_TICKET_LABELS.LOADING_ISSUE_FIELDS}
          </Typography>
        </Box>
      ) : (
        <NRJiraDynamicForm
          ref={dynamicFormRef}
          schema={issueFields}
          rpcOptions={rpcOptions}
          disabled={!fieldsAfterIssueTypeEnabled}
          onValidityChange={setDynamicFieldsValid}
          onRefresh={handleRefreshIssueFields}
          isRefreshing={isIssueFieldsLoading}
        />
      )}
    </>
  );
}

export default NRCreateVulnerabilityJiraTicketFields;
